import { useEffect, useState } from "react";
import {
    Phone,
    MessageCircle,
    MapPin,
    Clock,
    ShieldCheck,
    Truck,
    Car,
    Sparkles,
    Route as RouteIcon,
    ChevronRight,
    Menu,
    X,
    CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "sonner";

const HERO_IMG = "/grua-hero.jpg";
const IMG_STANDARD = "https://images.unsplash.com/photo-1686966933735-305bd8fe0a77?auto=format&fit=crop&w=1200&q=80";
const IMG_ACCIDENT = "https://images.unsplash.com/photo-1592588221162-d0d872c34bf1?auto=format&fit=crop&w=1200&q=80";
const IMG_LUXURY = "https://images.pexels.com/photos/14843802/pexels-photo-14843802.jpeg?auto=compress&cs=tinysrgb&w=1200";
const IMG_LONG = "https://images.pexels.com/photos/8931963/pexels-photo-8931963.jpeg?auto=compress&cs=tinysrgb&w=1200";
const IMG_BARCELONA = "https://images.unsplash.com/photo-1583422409516-2895a77efded?auto=format&fit=crop&w=1600&q=80";

const PHONES = [
    { label: "Móvil 1", number: "606 451 582", raw: "+34606451582" },
    { label: "Móvil 2", number: "658 772 695", raw: "+34658772695" },
    { label: "Móvil 3", number: "682 171 280", raw: "+34682171280" },
];

const WHATSAPP_NUMBER = "+34606451582";
const ADDRESS = "Passatge Jorbà, 12 · 08017 Barcelona";

const SERVICES = [
    {
        title: "Transporte en grúa plataforma",
        desc: "Traslado seguro de turismos, furgonetas y vehículos especiales. Plataforma plana, sin arrastre, sin daños.",
        img: IMG_STANDARD,
        icon: Truck,
        tag: "Más solicitado",
    },
    {
        title: "Asistencia a accidentes y averías",
        desc: "Recogida rápida de vehículos accidentados o averiados. Coordinación con aseguradoras y talleres.",
        img: IMG_ACCIDENT,
        icon: ShieldCheck,
        tag: "Urgencias",
    },
    {
        title: "Vehículos de lujo y clásicos",
        desc: "Trato VIP, correas blandas y máxima discreción y cuidado.",
        img: IMG_LUXURY,
        icon: Sparkles,
        tag: "Premium",
    },
    {
        title: "Largas distancias y servicios especiales",
        desc: "Transporte interurbano por Cataluña, Andorra y provincias colindantes. Rutas planificadas, entrega puntual y servicios a medida bajo demanda.",
        img: IMG_LONG,
        icon: RouteIcon,
        tag: "Radio de acción",
    },
];

const COVERAGE = [
    "Barcelona ciudad y área metropolitana",
    "Cataluña (Barcelona, Girona, Tarragona, Lleida)",
    "Andorra",
    "Provincias colindantes",
];

const REASONS = [
    {
        icon: Clock,
        title: "Respuesta inmediata",
        desc: "Equipos en ruta listos para atenderle al instante.",
    },
    {
        icon: ShieldCheck,
        title: "Servicio asegurado",
        desc: "Empresa autorizada, vehículos asegurados y personal cualificado.",
    },
    {
        icon: Car,
        title: "Flota especializada",
        desc: "Grúas plataforma y vehículos adaptados a cada tipo de carga.",
    },
    {
        icon: RouteIcon,
        title: "Radio de acción",
        desc: "Cataluña, Andorra y provincias colindantes con rutas optimizadas y servicios especiales a demanda.",
    },
];

const NAV_LINKS = [
    { label: "Servicios", href: "#servicios" },
    { label: "Cobertura", href: "#cobertura" },
    { label: "Por qué nosotros", href: "#nosotros" },
    { label: "Contacto", href: "#contacto" },
];

function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-white/90 border-b border-slate-200 shadow-sm"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
                <a
                    href="#top"
                    data-testid="brand-logo"
                    className="flex items-center gap-2 group"
                >
                    <div className="h-9 w-9 rounded-lg bg-blue-900 text-white flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
                        <Truck className="h-5 w-5" strokeWidth={2} />
                    </div>
                    <div className="leading-tight">
                        <div
                            className={`font-heading font-bold text-base sm:text-lg ${
                                scrolled ? "text-slate-900" : "text-white"
                            }`}
                        >
                            Grues Sant Antoni
                        </div>
                        <div
                            className={`text-[10px] sm:text-xs uppercase tracking-widest ${
                                scrolled ? "text-slate-500" : "text-blue-100"
                            }`}
                        >
                            Transporte de coches
                        </div>
                    </div>
                </a>

                <nav className="hidden lg:flex items-center gap-8">
                    {NAV_LINKS.map((link) => (
                        <a
                            key={link.href}
                            href={link.href}
                            data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={`text-sm font-medium transition-colors ${
                                scrolled
                                    ? "text-slate-700 hover:text-blue-700"
                                    : "text-white/90 hover:text-white"
                            }`}
                        >
                            {link.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <Button
                        asChild
                        data-testid="header-call-btn"
                        className="hidden sm:inline-flex bg-blue-700 hover:bg-blue-800 text-white h-10 px-4 rounded-full shadow-sm"
                    >
                        <a href={`tel:${PHONES[0].raw}`}>
                            <Phone className="h-4 w-4" strokeWidth={2} />
                            Llamar ahora
                        </a>
                    </Button>

                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                data-testid="mobile-menu-trigger"
                                className={`lg:hidden h-10 w-10 rounded-full inline-flex items-center justify-center transition ${
                                    scrolled
                                        ? "text-slate-900 hover:bg-slate-100"
                                        : "text-white hover:bg-white/10"
                                }`}
                                aria-label="Abrir menú"
                            >
                                <Menu className="h-5 w-5" />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[300px] bg-white">
                            <div className="flex flex-col h-full">
                                <div className="flex items-center justify-between pb-6 border-b border-slate-200">
                                    <span className="font-heading text-lg font-bold text-slate-900">
                                        Menú
                                    </span>
                                    <button
                                        onClick={() => setOpen(false)}
                                        aria-label="Cerrar"
                                        className="h-9 w-9 rounded-full hover:bg-slate-100 inline-flex items-center justify-center"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                                <nav className="flex flex-col gap-1 mt-6">
                                    {NAV_LINKS.map((link) => (
                                        <a
                                            key={link.href}
                                            href={link.href}
                                            onClick={() => setOpen(false)}
                                            data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                                            className="px-3 py-3 rounded-lg text-slate-800 font-medium hover:bg-slate-50 flex items-center justify-between"
                                        >
                                            {link.label}
                                            <ChevronRight className="h-4 w-4 text-slate-400" />
                                        </a>
                                    ))}
                                </nav>
                                <div className="mt-auto space-y-2 pb-2">
                                    <Button
                                        asChild
                                        data-testid="mobile-call-btn"
                                        className="w-full bg-blue-700 hover:bg-blue-800 text-white h-12 rounded-full"
                                    >
                                        <a href={`tel:${PHONES[0].raw}`}>
                                            <Phone className="h-4 w-4" />
                                            Llamar ahora
                                        </a>
                                    </Button>
                                    <Button
                                        asChild
                                        data-testid="mobile-whatsapp-btn"
                                        className="w-full bg-green-600 hover:bg-green-700 text-white h-12 rounded-full"
                                    >
                                        <a
                                            href={`https://wa.me/${WHATSAPP_NUMBER.replace("+", "")}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <MessageCircle className="h-4 w-4" />
                                            WhatsApp
                                        </a>
                                    </Button>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}

function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-[92vh] flex items-end overflow-hidden"
        >
            <img
                src={HERO_IMG}
                alt="Grúa transportando un coche"
                className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-blue-950/75 to-blue-900/50" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(37,99,235,0.25),transparent_60%)]" />

            <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 pt-32">
                <div className="max-w-3xl animate-fade-up">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-xs sm:text-sm font-medium text-white mb-6">
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 live-dot"></span>
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500"></span>
                        </span>
                        Servicio disponible · Barcelona
                    </div>

                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6">
                        Transporte de coches
                        <span className="block text-blue-300">
                            seguro y profesional.
                        </span>
                    </h1>

                    <p className="text-lg sm:text-xl text-blue-50/90 max-w-2xl mb-10 leading-relaxed">
                        Grúas plataforma con base en Barcelona. Asistencia y
                        traslados por Cataluña, Andorra y provincias
                        colindantes, con servicios especiales a demanda.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <Button
                            asChild
                            data-testid="hero-call-btn"
                            className="h-14 px-7 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-base font-semibold shadow-lg shadow-blue-900/40 group"
                        >
                            <a href={`tel:${PHONES[0].raw}`}>
                                <Phone className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                                Llamar al {PHONES[0].number}
                            </a>
                        </Button>

                        <Button
                            asChild
                            data-testid="hero-whatsapp-btn"
                            className="h-14 px-7 rounded-full bg-green-600 hover:bg-green-500 text-white text-base font-semibold shadow-lg shadow-green-900/40"
                        >
                            <a
                                href={`https://wa.me/${WHATSAPP_NUMBER.replace("+", "")}?text=Hola,%20necesito%20servicio%20de%20gr%C3%BAa.`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <MessageCircle className="h-5 w-5" />
                                WhatsApp directo
                            </a>
                        </Button>
                    </div>

                    <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4 max-w-2xl">
                        {[
                            ["+30", "años de experiencia"],
                            ["Flexible", "disponibilidad"],
                            ["Amplio", "radio de acción"],
                            ["100%", "vehículos asegurados"],
                        ].map(([n, l]) => (
                            <div key={l} data-testid={`hero-stat-${l.replace(/\s/g, "-")}`}>
                                <div className="font-heading text-2xl sm:text-3xl font-bold text-white">
                                    {n}
                                </div>
                                <div className="text-xs sm:text-sm text-blue-100/80 leading-tight">
                                    {l}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function Services() {
    return (
        <section
            id="servicios"
            data-testid="services-section"
            className="py-20 sm:py-24 lg:py-32 bg-slate-50"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mb-14 sm:mb-16">
                    <Badge
                        variant="secondary"
                        className="bg-blue-100 text-blue-800 hover:bg-blue-100 border-0 mb-4 px-3 py-1 rounded-full"
                    >
                        Nuestros servicios
                    </Badge>
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4">
                        Soluciones de transporte adaptadas a cada vehículo.
                    </h2>
                    <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                        Desde una avería urgente hasta el traslado de un coche
                        clásico. Trabajamos con cuidado, rapidez y la mejor
                        flota.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                    {SERVICES.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <Card
                                key={s.title}
                                data-testid={`service-card-${i}`}
                                className="overflow-hidden border border-slate-200 bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group rounded-2xl"
                            >
                                <div className="relative aspect-[16/10] overflow-hidden">
                                    <img
                                        src={s.img}
                                        alt={s.title}
                                        className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
                                    <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-xs font-semibold text-blue-800 shadow-sm">
                                        {s.tag}
                                    </span>
                                </div>
                                <div className="p-6 sm:p-7">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                                            <Icon
                                                className="h-5 w-5"
                                                strokeWidth={2}
                                            />
                                        </div>
                                        <h3 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">
                                            {s.title}
                                        </h3>
                                    </div>
                                    <p className="text-slate-600 leading-relaxed">
                                        {s.desc}
                                    </p>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function Coverage() {
    return (
        <section
            id="cobertura"
            data-testid="coverage-section"
            className="py-20 sm:py-24 lg:py-32 bg-white"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div className="order-2 lg:order-1">
                        <Badge
                            variant="secondary"
                            className="bg-blue-100 text-blue-800 hover:bg-blue-100 border-0 mb-4 px-3 py-1 rounded-full"
                        >
                            Dónde estamos
                        </Badge>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-5">
                            Base en Barcelona,
                            <span className="block text-blue-700">
                                radio de acción amplio.
                            </span>
                        </h2>
                        <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8">
                            Operamos desde el corazón de Barcelona, ofreciendo
                            servicio prioritario en toda Cataluña, Andorra y
                            provincias colindantes. Servicios especiales a
                            medida bajo demanda.
                        </p>

                        <div className="flex items-start gap-3 p-5 rounded-2xl bg-slate-50 border border-slate-200 mb-6">
                            <div className="h-10 w-10 rounded-lg bg-blue-900 text-white flex items-center justify-center flex-shrink-0">
                                <MapPin className="h-5 w-5" strokeWidth={2} />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-slate-500 mb-0.5">
                                    Dirección
                                </div>
                                <div
                                    data-testid="address-line"
                                    className="font-heading text-lg font-semibold text-slate-900"
                                >
                                    {ADDRESS}
                                </div>
                            </div>
                        </div>

                        <ul className="space-y-3">
                            {COVERAGE.map((c) => (
                                <li
                                    key={c}
                                    data-testid={`coverage-${c.split(" ")[0].toLowerCase()}`}
                                    className="flex items-center gap-3 text-slate-700"
                                >
                                    <CheckCircle2
                                        className="h-5 w-5 text-blue-700 flex-shrink-0"
                                        strokeWidth={2}
                                    />
                                    <span className="font-medium">{c}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-8">
                            <Button
                                asChild
                                data-testid="coverage-map-btn"
                                variant="outline"
                                className="h-12 px-5 rounded-full border-slate-300 hover:bg-slate-50 text-slate-900"
                            >
                                <a
                                    href="https://www.google.com/maps/search/?api=1&query=Passatge+Jorb%C3%A0+12+Barcelona"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <MapPin className="h-4 w-4" />
                                    Ver en Google Maps
                                </a>
                            </Button>
                        </div>
                    </div>

                    <div className="order-1 lg:order-2 relative">
                        <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl shadow-blue-900/20">
                            <img
                                src={IMG_BARCELONA}
                                alt="Barcelona"
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-blue-950/70 via-blue-900/10 to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6 text-white">
                                <div className="text-xs uppercase tracking-widest text-blue-200 mb-2">
                                    Sede central
                                </div>
                                <div className="font-heading text-3xl sm:text-4xl font-bold leading-tight">
                                    Barcelona
                                </div>
                                <div className="text-blue-100/90 mt-1">
                                    Pasaje Jorbà 12 · 08017
                                </div>
                            </div>
                        </div>

                        <div className="hidden sm:flex absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-slate-200 p-4 items-center gap-3 max-w-[260px]">
                            <div className="h-12 w-12 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                                <MapPin className="h-6 w-6" strokeWidth={2} />
                            </div>
                            <div>
                                <div className="font-heading font-bold text-slate-900">
                                    08017 Barcelona
                                </div>
                                <div className="text-xs text-slate-500">
                                    Passatge Jorbà, 12
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function WhyUs() {
    return (
        <section
            id="nosotros"
            data-testid="whyus-section"
            className="py-20 sm:py-24 lg:py-32 bg-slate-900 text-white relative overflow-hidden"
        >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(37,99,235,0.25),transparent_60%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(30,58,138,0.35),transparent_60%)]" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mb-14">
                    <Badge className="bg-blue-500/15 text-blue-200 hover:bg-blue-500/15 border border-blue-400/30 mb-4 px-3 py-1 rounded-full">
                        Por qué elegirnos
                    </Badge>
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
                        Experiencia, rapidez y total tranquilidad.
                    </h2>
                    <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                        Una empresa con décadas de trayectoria al
                        servicio del transporte de vehículos. Profesionalidad
                        que se nota en cada traslado.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {REASONS.map((r, i) => {
                        const Icon = r.icon;
                        return (
                            <div
                                key={r.title}
                                data-testid={`reason-card-${i}`}
                                className="p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors"
                            >
                                <div className="h-12 w-12 rounded-xl bg-blue-600 text-white flex items-center justify-center mb-4">
                                    <Icon className="h-6 w-6" strokeWidth={2} />
                                </div>
                                <h3 className="font-heading text-lg sm:text-xl font-semibold mb-2">
                                    {r.title}
                                </h3>
                                <p className="text-slate-300 text-sm leading-relaxed">
                                    {r.desc}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function Contact() {
    const copyPhone = (number) => {
        navigator.clipboard?.writeText(number);
        toast.success("Número copiado", {
            description: number,
        });
    };

    return (
        <section
            id="contacto"
            data-testid="contact-section"
            className="py-20 sm:py-24 lg:py-32 bg-white"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mb-12">
                    <Badge
                        variant="secondary"
                        className="bg-blue-100 text-blue-800 hover:bg-blue-100 border-0 mb-4 px-3 py-1 rounded-full"
                    >
                        Contacto directo
                    </Badge>
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4">
                        ¿Necesitas una grúa? Llámanos ahora.
                    </h2>
                    <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                        Sin formularios ni esperas. Marca cualquiera de
                        nuestros teléfonos y un equipo se pondrá en marcha.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
                    {PHONES.map((p, i) => (
                        <Card
                            key={p.raw}
                            data-testid={`phone-card-${i}`}
                            className="p-6 sm:p-7 border border-slate-200 rounded-2xl hover:shadow-xl hover:-translate-y-1 hover:border-blue-300 transition-all duration-300 group"
                        >
                            <div className="flex items-center justify-between mb-5">
                                <div className="h-12 w-12 rounded-xl bg-blue-700 text-white flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                                    <Phone
                                        className="h-6 w-6"
                                        strokeWidth={2}
                                    />
                                </div>
                                <span className="text-xs uppercase tracking-widest text-slate-500">
                                    {p.label}
                                </span>
                            </div>
                            <a
                                href={`tel:${p.raw}`}
                                data-testid={`phone-link-${i}`}
                                className="block font-heading text-2xl sm:text-3xl font-bold text-slate-900 hover:text-blue-700 transition-colors mb-4 tracking-tight"
                            >
                                {p.number}
                            </a>
                            <div className="flex gap-2">
                                <Button
                                    asChild
                                    data-testid={`phone-call-btn-${i}`}
                                    className="flex-1 bg-blue-700 hover:bg-blue-800 h-11 rounded-full text-white"
                                >
                                    <a href={`tel:${p.raw}`}>
                                        <Phone className="h-4 w-4" />
                                        Llamar
                                    </a>
                                </Button>
                                <Button
                                    onClick={() => copyPhone(p.number)}
                                    data-testid={`phone-copy-btn-${i}`}
                                    variant="outline"
                                    className="h-11 rounded-full border-slate-300 text-slate-700"
                                >
                                    Copiar
                                </Button>
                            </div>
                        </Card>
                    ))}
                </div>

                <div className="grid grid-cols-1 gap-5">
                    <a
                        href={`https://wa.me/${WHATSAPP_NUMBER.replace("+", "")}?text=Hola,%20necesito%20servicio%20de%20gr%C3%BAa.`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="contact-whatsapp-btn"
                        className="p-6 rounded-2xl bg-green-600 hover:bg-green-700 text-white flex items-center justify-between group transition-colors shadow-lg shadow-green-900/20"
                    >
                        <div>
                            <div className="text-xs uppercase tracking-widest text-green-100 mb-1">
                                Respuesta rápida
                            </div>
                            <div className="font-heading text-xl sm:text-2xl font-bold">
                                Escríbenos por WhatsApp
                            </div>
                        </div>
                        <div className="h-14 w-14 rounded-full bg-white/15 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
                            <MessageCircle className="h-6 w-6" strokeWidth={2} />
                        </div>
                    </a>
                </div>
            </div>
        </section>
    );
}

function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="bg-slate-950 text-slate-300 pt-16 pb-8"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
                    <div className="md:col-span-2">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="h-10 w-10 rounded-lg bg-blue-700 text-white flex items-center justify-center">
                                <Truck className="h-5 w-5" strokeWidth={2} />
                            </div>
                            <div className="font-heading font-bold text-lg text-white">
                                Grues Sant Antoni S.L.
                            </div>
                        </div>
                        <p className="text-slate-400 max-w-md leading-relaxed">
                            Empresa de transporte de vehículos con
                            base en Barcelona. Radio de acción en Cataluña,
                            Andorra y provincias colindantes, con servicios
                            especiales a demanda.
                        </p>
                    </div>

                    <div>
                        <div className="font-heading font-semibold text-white mb-3">
                            Contacto
                        </div>
                        <ul className="space-y-2 text-sm">
                            {PHONES.map((p) => (
                                <li key={p.raw}>
                                    <a
                                        data-testid={`footer-phone-${p.raw}`}
                                        href={`tel:${p.raw}`}
                                        className="hover:text-white transition-colors"
                                    >
                                        {p.number}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className="font-heading font-semibold text-white mb-3">
                            Dirección
                        </div>
                        <p className="text-sm text-slate-400 leading-relaxed">
                            Passatge Jorbà, 12
                            <br />
                            08017 Barcelona
                            <br />
                            España
                        </p>
                    </div>
                </div>

                <Separator className="bg-slate-800" />

                <div className="pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <p className="text-xs text-slate-500">
                        © {new Date().getFullYear()} Grues Sant Antoni S.L. ·
                        Todos los derechos reservados.
                    </p>
                    <p className="text-xs text-slate-500">
                        www.gruessantantoni.com
                    </p>
                </div>
            </div>
        </footer>
    );
}

function FloatingActions() {
    return (
        <div
            data-testid="floating-actions"
            className="fixed bottom-4 left-4 z-40 flex flex-col gap-2 sm:bottom-6 sm:left-6"
        >
            <a
                href={`https://wa.me/${WHATSAPP_NUMBER.replace("+", "")}?text=Hola,%20necesito%20servicio%20de%20gr%C3%BAa.`}
                target="_blank"
                rel="noreferrer"
                data-testid="floating-whatsapp"
                className="h-14 w-14 rounded-full bg-green-600 hover:bg-green-500 text-white flex items-center justify-center shadow-lg shadow-green-900/40 transition-transform hover:scale-110"
                aria-label="WhatsApp"
            >
                <MessageCircle className="h-6 w-6" strokeWidth={2} />
            </a>
            <a
                href={`tel:${PHONES[0].raw}`}
                data-testid="floating-call"
                className="h-14 w-14 rounded-full bg-blue-700 hover:bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-900/40 transition-transform hover:scale-110"
                aria-label="Llamar"
            >
                <Phone className="h-6 w-6" strokeWidth={2} />
            </a>
        </div>
    );
}

export default function Landing() {
    return (
        <div className="min-h-screen bg-white text-slate-900 font-body">
            <Header />
            <main>
                <Hero />
                <Services />
                <Coverage />
                <WhyUs />
                <Contact />
            </main>
            <Footer />
            <FloatingActions />
        </div>
    );
}
